import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';

// The type/storage key predate HubSpot and keep their names: the payload's
// `service` field is what tells Salesforce and HubSpot results apart.
const OAUTH_RESULT_TYPE = 'privacypal:salesforce-oauth-result';
const OAUTH_RESULT_STORAGE_KEY = 'privacypal:salesforce-oauth-result';

// Display names for the services whose OAuth callback lands here.
const SERVICE_NAMES: Record<string, string> = { salesforce: 'Salesforce', hubspot: 'HubSpot' };

export default function SalesforceOAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const [success, setSuccess] = useState(false);
  const [errorText, setErrorText] = useState('');
  const serviceName = SERVICE_NAMES[searchParams.get('service') || 'salesforce'] || 'Salesforce';

  useEffect(() => {
    const ok = searchParams.get('success') === 'true';
    const service = searchParams.get('service') || 'salesforce';
    const error = searchParams.get('error') || undefined;
    setSuccess(ok);
    if (!ok && error) setErrorText(error);

    const payload = { type: OAUTH_RESULT_TYPE, success: ok, service, error };

    // Report the result back to the original wizard tab. postMessage is the
    // primary channel; the localStorage write is a fallback for cases where the
    // opener reference is unavailable (the wizard tab listens for `storage`).
    try {
      if (window.opener) {
        window.opener.postMessage(payload, window.location.origin);
      }
    } catch {
      // opener may be cross-origin or gone — fall through to the storage channel.
    }
    try {
      // Remove first so an identical consecutive payload still fires `storage`.
      localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
      localStorage.setItem(OAUTH_RESULT_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage may be unavailable (private mode); postMessage already tried.
    }
  }, [searchParams]);

  return (
    <div className="p-8" style={{ fontFamily: "'Inter', sans-serif", minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="text-center max-w-sm">
        {success ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center mb-4" style={{ margin: '0 auto 1rem' }}>
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h1 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white"><Trans>{serviceName} authentication completed.</Trans></h1>
            <p className="text-sm text-slate-600 dark:text-slate-400"><Trans>You can close this window.</Trans></p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4" style={{ margin: '0 auto 1rem' }}>
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
            </div>
            <h1 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white"><Trans>{serviceName} authentication failed.</Trans></h1>
            {errorText && <p className="text-sm text-red-500 mb-1">{errorText}</p>}
            <p className="text-sm text-slate-600 dark:text-slate-400"><Trans>You can close this window and try again.</Trans></p>
          </>
        )}
        <button
          onClick={() => window.close()}
          className="mt-5 px-5 py-2.5 rounded-xl font-medium text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Trans>Close window</Trans>
        </button>
      </div>
    </div>
  );
}
